import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, FounderProfile as FounderProfileType } from '../services/api';
import ProgressTimeline from './ProgressTimeline';
import authBackground from '../assets/auth-background.jpg';
import './FounderProfile.css';

interface FounderProfileProps {
  founderId: string;
  onBack?: () => void; // Keep for backward compatibility
}

const FounderProfile: React.FC<FounderProfileProps> = ({ founderId, onBack }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<FounderProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRecentlyAdded, setIsRecentlyAdded] = useState(false);
  const [aiTimeline, setAiTimeline] = useState<any[]>([]);
  const [aiTimelineLoading, setAiTimelineLoading] = useState(false);
  const [aiTimelineError, setAiTimelineError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [linkedinPosts, setLinkedinPosts] = useState<any[]>([]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiService.getFounderProfile(founderId);
        setProfile(data);
        
        // Check if this founder was recently added (within last 10 minutes) and has no posts
        if (data.founder && data.timeline.length === 0) {
          const createdAt = new Date(data.founder.createdAt);
          const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
          setIsRecentlyAdded(createdAt > tenMinutesAgo);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch founder profile');
        console.error('Error fetching founder profile:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchAITimeline = async () => {
      try {
        setAiTimelineLoading(true);
        setAiTimelineError(null);
        const data = await apiService.getFounderAITimeline(founderId, 20);
        setAiTimeline(data.posts || []);
      } catch (err) {
        setAiTimelineError(err instanceof Error ? err.message : 'Failed to fetch AI timeline');
        console.error('Error fetching AI timeline:', err);
      } finally {
        setAiTimelineLoading(false);
      }
    };

    const fetchLinkedinPosts = async () => {
      try {
        const posts = await apiService.getLinkedinPosts(founderId);
        setLinkedinPosts(posts || []);
      } catch (err) {
        // Silent fail – fallback is best-effort
      }
    };

    const fetchTasks = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/tasks?founderId=${founderId}`;
        console.log('Fetching tasks from:', apiUrl);
        
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Tasks received:', data.tasks);
          setTasks(data.tasks || []);
        }
      } catch (err) {
        console.error('Error fetching tasks:', err);
      }
    };

    fetchProfile();
    fetchAITimeline();
    fetchLinkedinPosts();
    fetchTasks();

    // Set up polling to refetch tasks every 5 seconds
    const taskPollInterval = setInterval(() => {
      fetchTasks();
    }, 5000);

    // Cleanup interval on unmount
    return () => {
      clearInterval(taskPollInterval);
    };
  }, [founderId]);

  // Briefly poll for AI timeline if we have raw posts but no summaries yet
  useEffect(() => {
    if (linkedinPosts.length > 0 && aiTimeline.length === 0) {
      let attempts = 0;
      const maxAttempts = 6; // ~2 minutes at 20s interval
      const interval = setInterval(async () => {
        attempts += 1;
        try {
          const data = await apiService.getFounderAITimeline(founderId, 20);
          if (data.posts && data.posts.length > 0) {
            setAiTimeline(data.posts);
            clearInterval(interval);
          } else if (attempts >= maxAttempts) {
            clearInterval(interval);
          }
        } catch {
          if (attempts >= maxAttempts) clearInterval(interval);
        }
      }, 20000);
      return () => clearInterval(interval);
    }
  }, [linkedinPosts.length, aiTimeline.length, founderId]);

  useEffect(() => {
    const handleScroll = () => {
      const timeline = document.querySelector('.timeline') as HTMLElement;
      if (!timeline) return;

      // Use window scroll position
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      
      // Get timeline position in viewport
      const timelineRect = timeline.getBoundingClientRect();
      const timelineTop = timelineRect.top + scrollTop;
      const timelineHeight = timelineRect.height;
      
      // Calculate how much of the timeline has been scrolled
      const timelineScrollStart = timelineTop;
      const timelineScrollEnd = timelineTop + timelineHeight;
      const viewportBottom = scrollTop + clientHeight;
      
      // Calculate scroll progress from 0% to 100%
      // Add offset so animation starts later but still completes at bottom
      const startOffset = clientHeight * 0.5; // 50% of viewport height - animation start delay
      let scrollProgress = 0;
      if (viewportBottom > timelineScrollStart + startOffset) {
        const scrolledAmount = viewportBottom - timelineScrollStart - startOffset;
        const adjustedTimelineHeight = timelineHeight + startOffset; // Compensate for start delay
        scrollProgress = (scrolledAmount / adjustedTimelineHeight) * 100;
      }

      timeline.style.setProperty('--scroll-height', `${Math.min(Math.max(scrollProgress, 0), 100)}%`);

      // Update timeline item dots
      const timelineItems = document.querySelectorAll('.timeline-item');
      
      timelineItems.forEach((item) => {
        const itemRect = item.getBoundingClientRect();
        const itemMiddle = itemRect.top + itemRect.height / 2;
        const viewportMiddle = clientHeight / 2;
        
        // If the middle of the item is above the viewport middle, it's been scrolled past
        if (itemMiddle < viewportMiddle) {
          item.classList.add('scrolled');
        } else {
          item.classList.remove('scrolled');
        }
      });
    };

    // Attach to window scroll
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [profile]);

  const handleBackToDashboard = () => {
    if (onBack) {
      onBack();
    } else {
      // Use React Router navigation
      navigate('/people');
    }
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInHours < 168) { // 7 days
      const days = Math.floor(diffInHours / 24);
      return `${days}d ago`;
    } else {
      const monthDay = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      });
      const year = date.getFullYear();
      return `${monthDay}\n${year}`;
    }
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) { // 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days}d ago`;
    }
  };

  // Transform timeline data to ProgressTimeline format
  const getProgressTimelineSteps = () => {
    if (!profile) return [];

    const steps: any[] = [];

    // Add AI timeline items (LinkedIn posts) as completed steps
    if (aiTimeline.length > 0) {
      const aiSteps = aiTimeline.map((post, index) => ({
        id: `ai-${post.id}`,
        title: post.aiSummary,
        description: '', // No description needed since title is the summary
        date: formatDate(post.postTimestamp),
        timestamp: new Date(post.postTimestamp).getTime(),
        completed: true,
        status: 'completed' as const,
        details: post.postContent, // Show post content when expanded
        iconType: 'linkedin' as const,
        postUrl: post.postUrl,
        imageUrl: post.media?.imageUrl || undefined,
        videoUrl: post.media?.videoUrl || undefined
      }));
      steps.push(...aiSteps);
    } else if (linkedinPosts.length > 0) {
      // Fallback: show raw posts while summaries are pending
      const rawSteps = linkedinPosts.map((post: any) => ({
        id: `raw-${post.id}`,
        title: (post.postContent || '').slice(0, 140) || 'LinkedIn post',
        description: '',
        date: formatDate(post.postTimestamp),
        timestamp: new Date(post.postTimestamp).getTime(),
        completed: true,
        status: 'completed' as const,
        details: post.postContent || '',
        iconType: 'linkedin' as const,
        postUrl: post.postUrl,
        imageUrl: undefined,
        videoUrl: undefined
      }));
      steps.push(...rawSteps);
    }

    // Add tasks as timeline items
    if (tasks && tasks.length > 0) {
      const taskSteps = tasks.map((task) => ({
        id: `task-${task.id}`,
        title: task.title,
        description: '', // No description in collapsed view
        date: formatDate(task.createdAt),
        timestamp: new Date(task.createdAt).getTime(),
        completed: task.status === 'done',
        status: task.status === 'done' ? 'completed' as const : 'current' as const,
        details: task.description || '', // Show task description/body when expanded
        iconType: 'task' as const,
        metadata: {
          dueDate: task.dueDate
        }
      }));
      steps.push(...taskSteps);
    }

    // Sort by timestamp (most recent first)
    return steps.sort((a, b) => b.timestamp - a.timestamp);
  };

  // Cloud background style
  const cloudBgStyle = useMemo(
    () => ({ '--cloud-bg': `url(${authBackground})` } as React.CSSProperties),
    []
  );

  if (loading) {
    return (
      <div className="founder-profile-wrapper" style={cloudBgStyle}>
        <div className="founder-profile-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading founder profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="founder-profile-wrapper" style={cloudBgStyle}>
        <div className="founder-profile-container">
          <div className="error-state">
            <p>Error: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const { founder, timeline } = profile;

  return (
    <div className="founder-profile-wrapper" style={cloudBgStyle}>
      <div className="founder-profile-container">
      {/* Founder Info */}
      <div className="founder-info-content">
        <div className="founder-info-item">
          {founder.profileUrl ? (
            <a 
              href={founder.profileUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="founder-name-link"
            >
              <span className="founder-name">{founder.name}</span>
            </a>
          ) : (
            <span className="founder-name">{founder.name}</span>
          )}
        </div>
        <span className="founder-info-separator">|</span>
        <div className="founder-info-item">
          <span className="founder-company">{founder.company || 'No company'}</span>
        </div>
        {founder.tag && (
          <>
            <span className="founder-info-separator">|</span>
            <div className="founder-info-item">
              <span className="founder-tag-pill">{founder.tag}</span>
            </div>
          </>
        )}
      </div>

        {/* Progress Timeline */}
        {aiTimelineLoading ? (
          <div className="loading-updates">
            <div className="loading-spinner-small"></div>
            <div className="loading-text">
              <h3>Loading timeline...</h3>
              <p>Fetching the latest activity and AI summaries for this founder.</p>
            </div>
          </div>
        ) : aiTimelineError ? (
          <div className="error-state">
            <p>Error loading timeline: {aiTimelineError}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        ) : (isRecentlyAdded && aiTimeline.length === 0 && linkedinPosts.length === 0) ? (
          <div className="loading-updates">
            <div className="loading-spinner-small"></div>
            <div className="loading-text">
              <h3>Processing LinkedIn posts...</h3>
              <p>We're extracting this founder's recent posts and generating AI summaries. This usually takes 3-5 minutes.</p>
              <p className="loading-tip">💡 Refresh the page in a few minutes to see the updates!</p>
            </div>
          </div>
        ) : (
          <div className="timeline-section">
            <ProgressTimeline steps={getProgressTimelineSteps()} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FounderProfile;
